import {
  type AnalyticsMeasurementCheckpoint,
  AnalyticsTrackingDB
} from 'shared/services/analytics/AnalyticsTrackingDB'

import { ANALYTICS_CHECKPOINT_RETENTION_MS } from '../AnalyticsTracker.constants'
import {
  getNextUtcDayStart,
  isMeasurementCheckpoint
} from '../AnalyticsTracker.timing'
import type { AnalyticsTrackingView } from '../AnalyticsTracker.types'

export const isDocumentForeground = () =>
  document.visibilityState === 'visible' && document.hasFocus()

export const createMeasurementId = () => {
  try {
    return crypto.randomUUID()
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }
}

export const canRecoverCurrentDocumentMeasurement = () => {
  try {
    const navigationEntry = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined
    return (
      navigationEntry?.type === 'reload' ||
      navigationEntry?.type === 'back_forward'
    )
  } catch {
    return false
  }
}

export const readSessionStorage = (key: string) => {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

export const writeSessionStorage = (key: string, value: string) => {
  try {
    window.sessionStorage.setItem(key, value)
  } catch {
    // Analytics must continue even when browser storage is unavailable.
  }
}

export const removeSessionStorage = (key: string) => {
  try {
    window.sessionStorage.removeItem(key)
  } catch {
    // Analytics must continue even when browser storage is unavailable.
  }
}

export const parseCheckpoint = (serialized: string): unknown => {
  try {
    return JSON.parse(serialized)
  } catch {
    return undefined
  }
}

// One connection per page, shared by the shell and every frame tracker.
let sharedTrackingDB: AnalyticsTrackingDB | undefined
export const getSharedTrackingDB = () => {
  sharedTrackingDB ??= new AnalyticsTrackingDB()
  return sharedTrackingDB
}

export const msUntilNextUtcDay = () => {
  const now = Date.now()
  return Math.max(0, getNextUtcDayStart(now) - now)
}

const matchesTrackingView = (
  checkpoint: AnalyticsMeasurementCheckpoint,
  params: AnalyticsTrackingView
) =>
  checkpoint.path === params.path &&
  checkpoint.appId === params.appId &&
  checkpoint.appName === params.appName &&
  checkpoint.appPath === params.appPath &&
  checkpoint.userId === params.userId &&
  checkpoint.organizationId === params.organizationId &&
  checkpoint.originalOrganizationId === params.originalOrganizationId

export const canRestoreCheckpoint = (
  value: unknown,
  id: string,
  params: AnalyticsTrackingView,
  startedAt: number
): value is AnalyticsMeasurementCheckpoint =>
  isMeasurementCheckpoint(value) &&
  value.measurementId === id &&
  matchesTrackingView(value, params) &&
  value.updatedAt <= startedAt &&
  startedAt - value.updatedAt <= ANALYTICS_CHECKPOINT_RETENTION_MS
